import { useState, useRef, useCallback } from "react";
import { aimsApi } from "../repositories/aims";

interface AimsMappedTags {
  genres: string[];
  moods: string[];
  instruments: string[];
}

interface AimsItem {
  id: string;
  key: string;
  processed_at: string;
  title: string;
}

interface AimsInsightsResult {
  aimsRequestId: string;
  status: string;
  raw?: AimsMappedTags;
  mapped?: AimsMappedTags;
  suggestedArtists: string[];
  suggestedPlaylists: AimsItem[];
}

type AimsStatus = "idle" | "uploading" | "processing" | "partial" | "done" | "error";

const POLL_INTERVAL = 3000;
const MAX_POLL_ATTEMPTS = 60; // 3 minutes max

// TEMPORARY: AIMS sometimes prefixes artist names with "artist:" (e.g. "artist: child-wild").
// Strip it here until AIMS confirms the upstream fix is live, then delete this function.
const ARTIST_PREFIX = /^artist:\s*/i;
function stripArtistPrefix(name: string): string {
  return name.replace(ARTIST_PREFIX, "");
}

// AIMS returns collaboration tracks as a single artist entry with names joined
// by a comma (e.g. "Artist A, Artist B"). Split these into one name per artist
// so each can be suggested individually.
// e.g. splitCollaborations([{ title: "Artist A, Artist B" }, { title: "Artist C" }])
//      => ["Artist A", "Artist B", "Artist C"]
function splitCollaborations(artists: AimsItem[]): string[] {
  const names = artists.flatMap((artist) =>
    artist.title.split(",").map((name) => stripArtistPrefix(name.trim()))
  );
  return [...new Set(names)];
}

export function useAimsInsights() {
  const [status, setStatus] = useState<AimsStatus>("idle");
  const [result, setResult] = useState<AimsInsightsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);

  const cleanup = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const pollForResults = useCallback(
    (aimsRequestId: string) => {
      pollCountRef.current += 1;

      if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
        setStatus("partial");
        cleanup();
        return;
      }

      aimsApi
        .getTagResults(aimsRequestId)
        .then((response) => {
          if (response.data.success) {
            const pollData = response.data.data;
            setResult({
              ...pollData,
              suggestedArtists: splitCollaborations(
                (pollData.artists ?? []) as AimsItem[]
              ),
              suggestedPlaylists: (pollData.playlists ?? []) as AimsItem[],
            });
            setStatus("done");
            cleanup();
          } else {
            // Not ready yet, poll again
            pollTimerRef.current = setTimeout(
              () => pollForResults(aimsRequestId),
              POLL_INTERVAL
            );
          }
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            // Not ready yet, poll again
            pollTimerRef.current = setTimeout(
              () => pollForResults(aimsRequestId),
              POLL_INTERVAL
            );
          } else {
            setStatus("partial");
            cleanup();
          }
        });
    },
    [cleanup]
  );

  const analyze = useCallback(
    async (audioFile: File, title: string) => {
      cleanup();
      setStatus("uploading");
      setResult(null);
      setError(null);

      try {
        const response = await aimsApi.getAIInsights(audioFile, title);
        const data = response.data.data;
        const aimsRequestId = data?.id;

        if (!aimsRequestId) {
          throw new Error("No request ID received from AIMS");
        }

        // Artists, playlists and tags all arrive on the poll response; the
        // upload response only hands back the request id to poll with.
        setResult({
          aimsRequestId,
          status: "processing",
          suggestedArtists: [],
          suggestedPlaylists: [],
        });
        setStatus("processing");
        pollForResults(aimsRequestId);
      } catch (err: any) {
        setStatus("error");
        setError(
          err.response?.data?.message || err.message || "Failed to upload audio"
        );
      }
    },
    [cleanup, pollForResults]
  );

  // Re-hydrate from a request id saved on an existing pitch. Skips the upload
  // step entirely and polls the same endpoint analyze() polls.
  const restore = useCallback(
    (aimsRequestId: string) => {
      if (!aimsRequestId) return;
      cleanup();
      setError(null);
      setResult({
        aimsRequestId,
        status: "processing",
        suggestedArtists: [],
        suggestedPlaylists: [],
      });
      setStatus("processing");
      pollForResults(aimsRequestId);
    },
    [cleanup, pollForResults]
  );

  const reset = useCallback(() => {
    cleanup();
    setStatus("idle");
    setResult(null);
    setError(null);
  }, [cleanup]);

  return { status, result, error, analyze, restore, reset };
}
